// Constantes et fonctions pures partagées par le Relevé d'heures (container + reducer)

export const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export const HOURS_GRID = ['06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '00', '01', '02', '03', '04', '05'];
export const MINUTES_GRID = ['00', '30'];

export const PAUSE_OPTIONS = [
    { value: '00:00', label: 'Aucune' },
    { value: '00:30', label: '30 min' },
    { value: '01:00', label: '1h00' },
    { value: '01:30', label: '1h30' },
    { value: '02:00', label: '2h00' },
    { value: '02:30', label: '2h30' },
    { value: '03:00', label: '3h00' },
    { value: '03:30', label: '3h30' },
    { value: '04:00', label: '4h00' },
];

export const DEPLACEMENT_OPTIONS = [
    { value: '', label: 'Aucun' },
    { value: '00:30', label: '30 min' },
    { value: '01:00', label: '1h00' },
    { value: '01:30', label: '1h30' },
    { value: '02:00', label: '2h00' },
    { value: '02:30', label: '2h30' },
    { value: '03:00', label: '3h00' },
    { value: '03:30', label: '3h30' },
    { value: '04:00', label: '4h00' },
    { value: '04:30', label: '4h30' },
    { value: '05:00', label: '5h00' },
    { value: '05:30', label: '5h30' },
    { value: '06:00', label: '6h00' },
    { value: '06:30', label: '6h30' },
    { value: '07:00', label: '7h00' },
];

// Journée "type" appliquée par les raccourcis rapides (8h/j, pause 1h)
export const JOURNEE_TYPE = { debut: '08:30', fin: '17:30', pause: '01:00' };

export const timeToMinutes = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val * 60;
    if (typeof val === 'string') {
        if (val.includes(':')) {
            const [h, m] = val.split(':').map(Number);
            return (h || 0) * 60 + (m || 0);
        }
        const clean = val.replace(',', '.');
        const parsed = parseFloat(clean);
        if (!isNaN(parsed)) return parsed * 60;
    }
    return 0;
};

export const decimalHoursToHHMM = (decimal) => {
    if (!decimal || isNaN(decimal) || decimal <= 0) return '';
    const totalMin = Math.round(decimal * 60);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

export const parseDecimal = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const clean = val.toString().replace(',', '.');
    const parsed = parseFloat(clean);
    return isNaN(parsed) ? 0 : parsed;
};

// Heures de déplacement déclarées pour un jour, converties en décimal
export const depBrutHeures = (depBrutVal) => {
    const minutes = timeToMinutes(depBrutVal);
    return minutes > 0 ? parseFloat((minutes / 60).toFixed(2)) : 0;
};

// Déduction forfaitaire hebdomadaire (45 min trajet aller + 45 min trajet retour), appliquée une seule fois sur la semaine
export const DEDUCTION_TRAJET_DOMICILE = 1.5;

// Total du jour = (fin - début) - pause + déplacement
export const calculerTotalJour = (d) => {
    if (!d?.debut || !d?.fin) return 0;
    const minDebut = timeToMinutes(d.debut);
    const minFin = timeToMinutes(d.fin);
    const minPause = timeToMinutes(d.pause || '00:00');
    const minDep = timeToMinutes(d.depBrut || '');
    return parseFloat((Math.max(0, minFin - minDebut - minPause + minDep) / 60).toFixed(2));
};

// Total net de la semaine = somme des jours - déduction forfaitaire du trajet domicile/travail
export const calculerTotalSemaineNet = (totalBrutSemaine) => Math.max(0, parseFloat((totalBrutSemaine - DEDUCTION_TRAJET_DOMICILE).toFixed(2)));

// Au-delà de 6h de travail continu, une pause d'au moins 30 min est légalement obligatoire
export const PAUSE_MINIMUM_MINUTES = 30;
export const SEUIL_PAUSE_OBLIGATOIRE_MINUTES = 360;

export const pauseInsuffisante = (d, pauseCandidate) => {
    if (!d?.debut || !d?.fin) return false;
    const duree = timeToMinutes(d.fin) - timeToMinutes(d.debut);
    if (duree <= SEUIL_PAUSE_OBLIGATOIRE_MINUTES) return false;
    return timeToMinutes(pauseCandidate) < PAUSE_MINIMUM_MINUTES;
};

export const createEmptyDetails = () => JOURS.reduce((acc, jour) => {
    acc[jour.toLowerCase()] = { debut: '', fin: '', pause: '01:00', depBrut: '', obs: '' };
    return acc;
}, {});

export const getInitialWeekString = () => {
    const d = new Date();
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    return `${date.getUTCFullYear()}-W${weekNo.toString().padStart(2, '0')}`;
};

export const getWeekBoundaries = (weekStr) => {
    if (!weekStr) return { year: new Date().getFullYear(), week: 1, monday: new Date(), sunday: new Date() };
    const [yearStr, weekStrPart] = weekStr.split('-W');
    const year = parseInt(yearStr, 10);
    const week = parseInt(weekStrPart, 10);

    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const isoWeekStart = simple;
    if (dow <= 4) isoWeekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else isoWeekStart.setDate(simple.getDate() + 8 - simple.getDay());

    const monday = new Date(isoWeekStart);
    const sunday = new Date(isoWeekStart);
    sunday.setDate(monday.getDate() + 6);
    return { year, week, monday, sunday };
};

export const formatDate = (date) => date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

// Formate un datetime MySQL ("YYYY-MM-DD HH:MM:SS") en "JJ/MM/AAAA à HH:MM"
export const formatDateHeure = (value) => {
    if (!value) return '';
    const date = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
    if (isNaN(date.getTime())) return value;
    const jour = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const heure = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${jour} à ${heure}`;
};
